/**
 * MUI Component Overrides for PACS Application
 * Consistent, modern styling across all components
 */

export const getComponentOverrides = (palette, shadows) => ({
  // ===== BUTTONS =====
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        padding: '10px 20px',
        transition: 'all 150ms ease',
        '&:focus-visible': {
          outline: `2px solid ${palette.primary.main}`,
          outlineOffset: 2,
        },
      },
      contained: {
        '&:hover': {
          transform: 'translateY(-1px)',
        },
      },
      containedPrimary: {
        backgroundColor: palette.accent?.main || palette.primary.main,
        '&:hover': {
          backgroundColor: palette.accent?.dark || palette.primary.dark,
        },
      },
      outlined: {
        borderWidth: 1.5,
        '&:hover': {
          borderWidth: 1.5,
          backgroundColor: palette.action?.hover,
        },
      },
      outlinedPrimary: {
        borderColor: palette.primary.main,
        color: palette.primary.dark,
        '&:hover': {
          borderColor: palette.primary.dark,
          backgroundColor: `${palette.primary.main}10`,
        },
      },
      text: {
        '&:hover': {
          backgroundColor: palette.action?.hover,
        },
      },
      sizeSmall: {
        padding: '6px 14px',
        fontSize: '0.8125rem',
      },
      sizeLarge: {
        padding: '12px 28px',
        fontSize: '1rem',
      },
    },
  },

  // ===== ICON BUTTONS =====
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        transition: 'all 150ms ease',
        '&:hover': {
          backgroundColor: palette.action?.hover,
        },
        '&:focus-visible': {
          outline: `2px solid ${palette.primary.main}`,
          outlineOffset: 2,
        },
      },
    },
  },

  // ===== TEXT FIELDS =====
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'small',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          backgroundColor: palette.background?.subtle || palette.neutral?.[100] || '#F5F5F5',
          transition: 'all 150ms ease',
          '& fieldset': {
            borderColor: 'transparent',
            transition: 'all 150ms ease',
          },
          '&:hover fieldset': {
            borderColor: palette.neutral?.[300] || '#D4D4D4',
          },
          '&.Mui-focused fieldset': {
            borderColor: palette.primary.main,
            borderWidth: 2,
          },
          '&.Mui-error fieldset': {
            borderColor: palette.error?.main || '#EF4444',
          },
        },
        '& .MuiInputBase-input': {
          padding: '12px 14px',
        },
      },
    },
  },

  // ===== OUTLINED INPUT =====
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        backgroundColor: palette.background?.subtle || palette.neutral?.[100] || '#F5F5F5',
        '& fieldset': {
          borderColor: 'transparent',
        },
        '&:hover fieldset': {
          borderColor: palette.neutral?.[300] || '#D4D4D4',
        },
        '&.Mui-focused fieldset': {
          borderColor: palette.primary.main,
          borderWidth: 2,
        },
      },
      input: {
        padding: '12px 14px',
      },
    },
  },

  // ===== SELECT =====
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },

  // ===== CARDS =====
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: `1px solid ${palette.divider || '#E5E5E5'}`,
        backgroundColor: palette.background?.paper || '#ffffff',
        transition: 'all 200ms ease',
        '&:hover': {
          boxShadow: shadows?.[4] || '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
  },

  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '20px 24px 12px',
      },
      title: {
        fontSize: '1.125rem',
        fontWeight: 600,
      },
      subheader: {
        fontSize: '0.875rem',
        color: palette.text?.secondary,
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '12px 24px 20px',
        '&:last-child': {
          paddingBottom: 24,
        },
      },
    },
  },

  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '12px 24px 20px',
        borderTop: `1px solid ${palette.divider || '#E5E5E5'}`,
      },
    },
  },

  // ===== PAPER =====
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: 12,
      },
      outlined: {
        borderColor: palette.divider || '#E5E5E5',
      },
    },
  },

  // ===== TABLES =====
  MuiTableContainer: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: `1px solid ${palette.divider || '#E5E5E5'}`,
        overflow: 'hidden',
      },
    },
  },

  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: palette.neutral?.[50] || '#FAFAFA',
        '& .MuiTableCell-head': {
          fontWeight: 600,
          fontSize: '0.8125rem',
          color: palette.text?.secondary || '#737373',
          letterSpacing: '0.02em',
          borderBottom: `1px solid ${palette.divider || '#E5E5E5'}`,
          padding: '14px 16px',
        },
      },
    },
  },

  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableRow-root': {
          transition: 'background-color 150ms ease',
          '&:hover': {
            backgroundColor: palette.action?.hover || 'rgba(0, 0, 0, 0.04)',
          },
          '&:nth-of-type(odd)': {
            backgroundColor: palette.neutral?.[50] || '#FAFAFA',
            '&:hover': {
              backgroundColor: palette.action?.hover || 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${palette.divider || '#E5E5E5'}`,
        padding: '14px 16px',
        fontSize: '0.875rem',
      },
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      root: {
        borderTop: `1px solid ${palette.divider || '#E5E5E5'}`,
      },
      selectLabel: {
        fontSize: '0.875rem',
      },
      displayedRows: {
        fontSize: '0.875rem',
      },
    },
  },

  // ===== DIALOGS / MODALS =====
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
        boxShadow: shadows?.[16] || '0 20px 25px -5px rgb(0 0 0 / 0.1)',
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '1.25rem',
        fontWeight: 600,
        padding: '24px 24px 16px',
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px 24px',
        gap: 12,
      },
    },
  },

  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
      },
    },
  },

  // ===== CHIPS =====
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontWeight: 500,
        fontSize: '0.75rem',
      },
      filled: {
        backgroundColor: palette.neutral?.[100] || '#F5F5F5',
      },
      outlined: {
        borderColor: palette.neutral?.[300] || '#D4D4D4',
      },
      colorSuccess: {
        backgroundColor: palette.success?.light || '#D1FAE5',
        color: palette.success?.dark || '#059669',
      },
      colorError: {
        backgroundColor: palette.error?.light || '#FEE2E2',
        color: palette.error?.dark || '#DC2626',
      },
      colorWarning: {
        backgroundColor: palette.warning?.light || '#FEF3C7',
        color: palette.warning?.dark || '#D97706',
      },
      colorInfo: {
        backgroundColor: palette.info?.light || '#DBEAFE',
        color: palette.info?.dark || '#2563EB',
      },
      colorPrimary: {
        backgroundColor: palette.primary?.[100] || '#E6F7FF',
        color: palette.primary?.[800] || '#0050B3',
      },
    },
  },

  // ===== AVATARS =====
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        fontSize: '0.875rem',
      },
      colorDefault: {
        backgroundColor: palette.primary.main,
        color: '#ffffff',
      },
    },
  },

  // ===== TABS =====
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 44,
      },
      indicator: {
        height: 2,
        borderRadius: 1,
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        minHeight: 44,
        padding: '12px 16px',
        '&.Mui-selected': {
          color: palette.primary.dark,
        },
      },
    },
  },

  // ===== TOOLTIPS =====
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: palette.neutral?.[800] || '#262626',
        color: '#ffffff',
        fontSize: '0.75rem',
        fontWeight: 500,
        padding: '6px 12px',
        borderRadius: 6,
      },
      arrow: {
        color: palette.neutral?.[800] || '#262626',
      },
    },
  },

  // ===== ALERTS =====
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '12px 16px',
      },
      standardSuccess: {
        backgroundColor: palette.success?.light || '#D1FAE5',
        color: palette.success?.dark || '#059669',
      },
      standardError: {
        backgroundColor: palette.error?.light || '#FEE2E2',
        color: palette.error?.dark || '#DC2626',
      },
      standardWarning: {
        backgroundColor: palette.warning?.light || '#FEF3C7',
        color: palette.warning?.dark || '#D97706',
      },
      standardInfo: {
        backgroundColor: palette.info?.light || '#DBEAFE',
        color: palette.info?.dark || '#2563EB',
      },
    },
  },

  // ===== LIST ITEMS =====
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        transition: 'all 150ms ease',
        '&:hover': {
          backgroundColor: palette.action?.hover,
        },
        '&.Mui-selected': {
          backgroundColor: `${palette.primary.main}15`,
          '&:hover': {
            backgroundColor: `${palette.primary.main}20`,
          },
        },
      },
    },
  },

  // ===== DRAWER =====
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: 'none',
        boxShadow: shadows?.[8] || '0 10px 15px -3px rgb(0 0 0 / 0.1)',
      },
    },
  },

  // ===== APP BAR =====
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundColor: palette.background?.paper || '#ffffff',
        color: palette.text?.primary,
        borderBottom: `1px solid ${palette.divider || '#E5E5E5'}`,
      },
    },
  },

  // ===== MENU =====
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        boxShadow: shadows?.[8] || '0 10px 15px -3px rgb(0 0 0 / 0.1)',
        border: `1px solid ${palette.divider || '#E5E5E5'}`,
        marginTop: 8,
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        padding: '10px 16px',
        borderRadius: 6,
        margin: '2px 8px',
        transition: 'background-color 150ms ease',
        '&:hover': {
          backgroundColor: palette.action?.hover,
        },
        '&.Mui-selected': {
          backgroundColor: `${palette.primary.main}15`,
          '&:hover': {
            backgroundColor: `${palette.primary.main}20`,
          },
        },
      },
    },
  },

  // ===== SKELETON =====
  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: palette.neutral?.[200] || '#E5E5E5',
      },
      rectangular: {
        borderRadius: 8,
      },
    },
  },

  // ===== DIVIDER =====
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: palette.divider || '#E5E5E5',
      },
    },
  },

  // ===== STEPPER =====
  MuiStepLabel: {
    styleOverrides: {
      label: {
        fontSize: '0.875rem',
        fontWeight: 500,
        '&.Mui-active': {
          fontWeight: 600,
        },
        '&.Mui-completed': {
          fontWeight: 500,
        },
      },
    },
  },

  MuiStepIcon: {
    styleOverrides: {
      root: {
        '&.Mui-active': {
          color: palette.primary.main,
        },
        '&.Mui-completed': {
          color: palette.success?.main || '#10B981',
        },
      },
    },
  },

  // ===== FORM CONTROLS =====
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: palette.text?.primary,
        marginBottom: 6,
        '&.Mui-focused': {
          color: palette.primary.dark,
        },
      },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontSize: '0.75rem',
        marginTop: 6,
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        fontWeight: 500,
      },
    },
  },

  // ===== CHECKBOX & RADIO =====
  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: palette.neutral?.[400] || '#A3A3A3',
        '&.Mui-checked': {
          color: palette.primary.main,
        },
      },
    },
  },

  MuiRadio: {
    styleOverrides: {
      root: {
        color: palette.neutral?.[400] || '#A3A3A3',
        '&.Mui-checked': {
          color: palette.primary.main,
        },
      },
    },
  },

  // ===== SWITCH =====
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 44,
        height: 24,
        padding: 0,
      },
      switchBase: {
        padding: 2,
        '&.Mui-checked': {
          transform: 'translateX(20px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            backgroundColor: palette.primary.main,
            opacity: 1,
          },
        },
      },
      thumb: {
        width: 20,
        height: 20,
      },
      track: {
        borderRadius: 12,
        backgroundColor: palette.neutral?.[300] || '#D4D4D4',
        opacity: 1,
      },
    },
  },

  // ===== BREADCRUMBS =====
  MuiBreadcrumbs: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
      },
      separator: {
        color: palette.neutral?.[400] || '#A3A3A3',
      },
    },
  },

  // ===== PAGINATION =====
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
        '&.Mui-selected': {
          backgroundColor: palette.primary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: palette.primary.dark,
          },
        },
      },
    },
  },

  // ===== BADGE =====
  MuiBadge: {
    styleOverrides: {
      badge: {
        fontWeight: 600,
        fontSize: '0.6875rem',
      },
    },
  },

  // ===== LINK =====
  MuiLink: {
    styleOverrides: {
      root: {
        color: palette.primary.dark,
        textDecorationColor: 'transparent',
        transition: 'text-decoration-color 150ms ease',
        '&:hover': {
          textDecorationColor: palette.primary.dark,
        },
      },
    },
  },

  // ===== AUTOCOMPLETE =====
  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        boxShadow: shadows?.[8] || '0 10px 15px -3px rgb(0 0 0 / 0.1)',
        border: `1px solid ${palette.divider || '#E5E5E5'}`,
      },
      option: {
        fontSize: '0.875rem',
        padding: '10px 16px',
        '&[aria-selected="true"]': {
          backgroundColor: `${palette.primary.main}15`,
        },
        '&:hover': {
          backgroundColor: palette.action?.hover,
        },
      },
    },
  },
});

export default getComponentOverrides;
